import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useMutationWithToast } from "@/shared/hooks/useMutationWithToast";
import { tagService, type KbTagDefinition } from "@/entities/tag";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { Badge } from "@/shared/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/shared/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/shared/ui/dialog";
import { useToast } from "@/shared/hooks/use-toast";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/shared/ui/tooltip";
import {
  Plus,
  Trash2,
  Loader2,
  Tag,
  Shield,
  Pencil,
  Check,
  X,
  RefreshCw,
  Info,
} from "lucide-react";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { errorToast } from "@/shared/lib/apiErrors";

interface TagManagementPanelProps {
  tenantCode?: string;
}

export function TagManagementPanel({ tenantCode }: TagManagementPanelProps = {}) {
  // V27 (2026-05-19) — KB 标签写操作收口给平台代客;租户角色仅可读。
  const { isImpersonating } = useImpersonation();
  const canManage = isImpersonating;
  const { toast } = useToast();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const CATEGORY_LABELS: Record<string, string> = {
    public: t("tagManagement.categoryPublic"),
    scene: t("tagManagement.categoryScene"),
  };
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ name: "" });
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState("");
  const [syncing, setSyncing] = useState(false);

  const { data: tags = [], isLoading } = useQuery({
    queryKey: ["kb-tags", tenantCode ?? null],
    queryFn: () => tagService.list(),
  });

  const createMutation = useMutationWithToast({
    mutationFn: tagService.create,
    successToast: t("tagManagement.toastCreateSuccess"),
    errorToast: t("tagManagement.toastCreateFailed"),
    invalidateKeys: [["kb-tags"]],
    onSuccess: () => {
      setAddOpen(false);
      setForm({ name: "" });
    },
  });

  const updateMutation = useMutationWithToast({
    mutationFn: ({ id, name }: { id: number; name: string }) => tagService.update(id, { name }),
    successToast: t("tagManagement.toastUpdateSuccess"),
    errorToast: t("tagManagement.toastUpdateFailed"),
    invalidateKeys: [["kb-tags"]],
    onSuccess: () => setEditingId(null),
  });

  const deleteMutation = useMutationWithToast({
    mutationFn: tagService.delete,
    successToast: t("tagManagement.toastDeleteSuccess"),
    errorToast: t("tagManagement.toastDeleteFailed"),
    invalidateKeys: [["kb-tags"]],
  });

  const handleCreate = () => {
    if (!form.name.trim()) {
      toast({ title: t("tagManagement.toastFillRequired"), variant: "destructive" });
      return;
    }
    // tag_value 由后端自动生成(对自建标签无人类语义),用户只填显示名。
    createMutation.mutate({ name: form.name.trim() });
  };

  const handleSyncScene = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const r = await tagService.syncScene(tenantCode);
      const conflictMsg =
        r.conflicts.length > 0
          ? t("tagManagement.toastSyncConflict", { count: r.conflicts.length })
          : "";
      toast({
        title: t("tagManagement.toastSyncDone"),
        description: t("tagManagement.toastSyncDesc", {
          added: r.added.length,
          updated: r.updated.length,
          removed: r.removed.length,
          conflict: conflictMsg,
        }),
      });
      await queryClient.invalidateQueries({ queryKey: ["kb-tags"] });
    } catch (e) {
      toast(errorToast(e, { fallbackTitle: t("tagManagement.toastSyncFailed") }));
    } finally {
      setSyncing(false);
    }
  };

  const publicTags = tags.filter((t) => t.category === "public");
  const sceneTags = tags.filter((t) => t.category === "scene");

  return (
    <TooltipProvider>
      <div className="space-y-4">
        {/* V27 — 自动打标提示 (租户角色看到的) */}
        {!canManage && (
          <div className="flex items-start gap-2 rounded-md border border-info/30 bg-info/5 p-2.5">
            <Info className="h-3.5 w-3.5 mt-0.5 text-info shrink-0" />
            <p className="text-2xs text-muted-foreground leading-relaxed">
              {t("tagManagement.autoTagNotice", {
                defaultValue: "标签由系统根据知识库内容自动生成,如需调整请联系平台运营。",
              })}
            </p>
          </div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Tag className="h-4 w-4 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">{t("tagManagement.panelHint")}</p>
          </div>
          {canManage && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleSyncScene}
                disabled={syncing}
                title={t("tagManagement.syncSceneTitle")}
              >
                {syncing ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-1" />
                )}
                {t("tagManagement.syncSceneBtn")}
              </Button>
              <Button size="sm" onClick={() => setAddOpen(true)}>
                <Plus className="h-4 w-4 mr-1" />
                {t("tagManagement.createTag")}
              </Button>
            </div>
          )}
        </div>

        {/* Tag Table */}
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-24">{t("tagManagement.colType")}</TableHead>
              <TableHead>{t("tagManagement.colName")}</TableHead>
              <TableHead>{t("tagManagement.colValue")}</TableHead>
              <TableHead className="w-20">{t("tagManagement.colAttr")}</TableHead>
              <TableHead className="w-20">{t("tagManagement.colActions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                  <Loader2 className="h-5 w-5 animate-spin mx-auto mb-1" />
                  {t("tagManagement.loading")}
                </TableCell>
              </TableRow>
            )}
            {!isLoading && tags.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                  {t("tagManagement.emptyHint")}
                </TableCell>
              </TableRow>
            )}
            {renderGroup(
              publicTags,
              "public",
              CATEGORY_LABELS,
              deleteMutation,
              editingId,
              editName,
              setEditingId,
              setEditName,
              updateMutation,
              t,
              canManage
            )}
            {renderGroup(
              sceneTags,
              "scene",
              CATEGORY_LABELS,
              deleteMutation,
              editingId,
              editName,
              setEditingId,
              setEditName,
              updateMutation,
              t,
              canManage
            )}
          </TableBody>
        </Table>

        {/* Create Dialog — public 类目 only (scene 由意图包管理) */}
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{t("tagManagement.createDialogTitle")}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-2xs text-muted-foreground">
                {t("tagManagement.createDialogHint")}
              </p>
              <div className="space-y-1.5">
                <Label>{t("tagManagement.fieldName")}</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder={t("tagManagement.fieldNamePlaceholder")}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && form.name.trim()) handleCreate();
                  }}
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setAddOpen(false)}>
                  {t("tagManagement.cancel")}
                </Button>
                <Button onClick={handleCreate} disabled={createMutation.isPending}>
                  {createMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
                  {t("tagManagement.createBtn")}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}

function renderGroup(
  tags: KbTagDefinition[],
  category: string,
  CATEGORY_LABELS: Record<string, string>,
  deleteMutation: ReturnType<typeof useMutation<void, Error, number>>,
  editingId: number | null,
  editName: string,
  setEditingId: (id: number | null) => void,
  setEditName: (name: string) => void,
  updateMutation: ReturnType<
    typeof useMutation<KbTagDefinition, Error, { id: number; name: string }>
  >,
  t: ReturnType<typeof useTranslation>["t"],
  canManage: boolean
) {
  return tags.map((tag) => {
    const isEditing = editingId === tag.id;
    const isScene = tag.category === "scene";
    return (
      <TableRow key={tag.id} data-testid="tag-row">
        <TableCell>
          <Badge variant={category === "public" ? "default" : "secondary"} className="text-2xs">
            {CATEGORY_LABELS[category] ?? category}
          </Badge>
        </TableCell>
        <TableCell className="font-medium">
          {isEditing && !isScene ? (
            // 内联编辑框打开即聚焦为有意 UX

            <Input
              autoFocus
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              className="h-7 w-40 text-sm"
              onKeyDown={(e) => {
                if (e.key === "Enter" && editName.trim()) {
                  updateMutation.mutate({ id: tag.id, name: editName.trim() });
                }
                if (e.key === "Escape") setEditingId(null);
              }}
            />
          ) : (
            tag.name
          )}
        </TableCell>
        <TableCell className="font-mono text-xs text-muted-foreground">{tag.tagValue}</TableCell>
        <TableCell>
          {isScene ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge
                  variant="outline"
                  className="text-2xs gap-1 cursor-help"
                  data-testid="system-sync-badge"
                >
                  <Shield className="h-3 w-3" />
                  {t("tagManagement.systemSyncBadge")}
                </Badge>
              </TooltipTrigger>
              <TooltipContent>{t("tagManagement.systemSyncTooltip")}</TooltipContent>
            </Tooltip>
          ) : (
            tag.isSystem && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Shield className="h-3 w-3" />
                {t("tagManagement.systemBadge")}
              </div>
            )
          )}
        </TableCell>
        <TableCell>
          {isScene || !canManage ? (
            <span className="text-2xs text-muted-foreground">—</span>
          ) : (
            <div className="flex gap-0.5">
              {isEditing ? (
                <>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-success hover:text-success/80"
                    disabled={!editName.trim() || updateMutation.isPending}
                    onClick={() => updateMutation.mutate({ id: tag.id, name: editName.trim() })}
                  >
                    <Check className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => setEditingId(null)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    title={t("tagManagement.rename")}
                    data-testid="edit-btn"
                    onClick={() => {
                      // 防御:name 为空也不让 editName 变 undefined,否则下面 editName.trim() 整页崩(缺陷报告)。
                      setEditingId(tag.id);
                      setEditName(tag.name ?? "");
                    }}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  {/* disabled 按钮 pointer-events-none,系统标签不可删的原因提示挂包装 span 才弹得出 */}
                  <span
                    title={tag.isSystem ? t("tagManagement.systemTagNoDelete") : undefined}
                    className="inline-block"
                  >
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      disabled={tag.isSystem || deleteMutation.isPending}
                      title={tag.isSystem ? undefined : t("tagManagement.deleteTag")}
                      data-testid="delete-btn"
                      onClick={() => {
                        if (confirm(t("tagManagement.confirmDelete", { name: tag.name }))) {
                          deleteMutation.mutate(tag.id);
                        }
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </span>
                </>
              )}
            </div>
          )}
        </TableCell>
      </TableRow>
    );
  });
}
